import React, { useMemo } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchResultCard } from "./SearchResultCard";
import { getTypeConfig } from "@/constants/ui-mapping";
import type { Tables } from "@/integrations/supabase/types";

type Document = Tables<"documents">;

interface SearchResult extends Document {
  matchScore?: number;
  matchReason?: string;
}

interface GroupedResults {
  type: string;
  label: string;
  emoji: string;
  docs: SearchResult[];
}

interface InlineSearchResultsProps {
  searchQuery: string;
  searchResults: SearchResult[];
  isLoading: boolean;
  onDocumentClick: (doc: Document) => void;
  totalCount?: number;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

export function InlineSearchResults({
  searchQuery,
  searchResults,
  isLoading,
  onDocumentClick,
  totalCount,
  hasMore,
  onLoadMore,
  isLoadingMore,
}: InlineSearchResultsProps) {
  // Use totalCount if provided, otherwise fall back to results length
  const displayTotal = totalCount ?? searchResults.length;
  const remainingCount = displayTotal - searchResults.length;
  // Group results by document type
  const groupedResults = useMemo((): GroupedResults[] => {
    const groups = new Map<string, SearchResult[]>();
    
    searchResults.forEach(doc => {
      const type = doc.doc_type || "other";
      if (!groups.has(type)) {
        groups.set(type, []);
      }
      groups.get(type)!.push(doc);
    });

    // Convert to array and sort by group size (largest first)
    return Array.from(groups.entries())
      .map(([type, docs]) => {
        const config = getTypeConfig(type);
        return {
          type,
          label: config.label,
          emoji: config.emoji,
          docs,
        };
      })
      .sort((a, b) => b.docs.length - a.docs.length);
  }, [searchResults]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
        <p className="text-sm text-muted-foreground">Recherche en cours...</p>
      </div>
    );
  }

  // Empty state
  if (searchResults.length === 0 && searchQuery) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4">
        <Sparkles className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground text-center">
          Aucun résultat pour "{searchQuery}"
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1 text-center">
          Essayez avec d'autres termes ou une description différente
        </p>
      </div>
    );
  }

  // Results grouped by type
  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      {/* Results header */}
      <div className="px-4 py-3 bg-muted/50 border-b border-border">
        <p className="text-sm font-medium text-foreground">
          {displayTotal} document{displayTotal > 1 ? 's' : ''} trouvé{displayTotal > 1 ? 's' : ''} pour "{searchQuery}"
        </p>
      </div>

      {/* Grouped results */}
      {groupedResults.map((group) => (
        <div key={group.type}>
          {/* Group header */}
          <div className="sticky top-0 z-[5] px-4 py-2 bg-muted/80 backdrop-blur-sm border-b border-border">
            <div className="flex items-center gap-2">
              <span className="text-base">{group.emoji}</span>
              <span className="text-xs font-medium text-foreground">
                {group.label}
              </span>
              <span className="text-xs text-muted-foreground">
                ({group.docs.length})
              </span>
            </div>
          </div>
          
          {/* Group items */}
          {group.docs.map((doc, index) => (
            <SearchResultCard
              key={doc.id}
              doc={doc}
              index={index}
              onClick={() => onDocumentClick(doc)}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      ))}

      {/* Load more button */}
      {hasMore && (
        <div className="p-4 border-t border-border">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : null}
            Voir plus ({remainingCount} restant{remainingCount > 1 ? 's' : ''})
          </Button>
        </div>
      )}
    </div>
  );
}